import { FC, useRef } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DownTrendIcon, UpTrendIcon } from "../../assets/icons";
import { formatNumber } from "../../common/utils";
import { MeasurableValueDTO } from "../../datasources/api-client/data-contracts";
import { ThemeColors } from "../../theme/themeValues";
import PaperCard from "../widgets/PaperCard";
import PaperCardHeader from "../widgets/PaperCard/PaperCardHeader";

interface Props {
	title?: string;
	delta?: MeasurableValueDTO | null;
	isLoading?: boolean;
	value?: number;
	measure?: string;
	invertDelta?: boolean;
	onReload?: VoidFunction;
	graph?: React.ReactNode;
	highlight?: string; // A small component to be displayed in the bottom right corner
	info?: string; // Info tooltip text
	coverage?: MeasurableValueDTO | null;
	isMarketBase?: boolean;
}

const NumericValueWidget: FC<Props> = ({
	title = "",
	delta,
	invertDelta = true,
	isLoading,
	onReload,
	value,
	measure,
	graph,
	highlight,
	info,
	coverage,
	isMarketBase,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("result.transport");

	const trendValue = value && value !== 0 && delta?.value ? delta.value / value : 0;

	const trendPercentage = formatNumber(trendValue, "percent", 2, 2);

	// The trend is up and is interpreted as a good trend (invert delta = false) - OR
	// The trend is down but is interpreted as a good trend (invert delta = true) -> Primary color
	// Otherwise -> Error color
	const trendColor = (trendValue > 0 && !invertDelta) || (trendValue < 0 && invertDelta) ? "success" : "error";

	const arrow =
		trendValue > 0 ? (
			<UpTrendIcon fontSize="small" color={trendColor} />
		) : (
			<DownTrendIcon fontSize="small" color={trendColor} />
		);

	const formattedValue = value ? formatNumber(value, "decimal", 0, 2) : 0;

	const valueContent = formattedValue !== undefined && (
		<Typography
			variant="h4"
			style={{ flexDirection: "row", display: "flex", alignItems: "flex-end" }}
			color="primary"
			fontWeight={"bold"}
		>
			{formattedValue}
			{measure && (
				<Typography pb={0.4} ml={1} variant="body2" color="text.secondary">
					{measure}
				</Typography>
			)}
		</Typography>
	);

	const trendContent = (
		<Stack direction={"row"} spacing={0.5} alignItems={"center"} pt={1}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					padding: 0.5,
					borderRadius: "50%",
					backgroundColor: ThemeColors.LightBackgroundElevation0,
				}}
			>
				{arrow}
			</Box>
			<Typography variant="body2" fontWeight="bold">
				{trendPercentage}
			</Typography>
			<Typography variant="caption" color="text.secondary" lineHeight={1.1}>
				{isMarketBase
					? t("difference compared to location-based emissions").toLowerCase()
					: t("last year").toLowerCase()}
			</Typography>
		</Stack>
	);

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					title={title}
					info={info}
					isLoading={isLoading}
					onReload={onReload}
					containerRef={containerRef}
				/>
			}
			content={
				<Stack direction={"row"} width={"100%"} height={"100%"} justifyContent={"space-between"} spacing={2}>
					<Stack flex={1.5} direction="column" justifyContent={"flex-end"} width={"100%"}>
						{valueContent}
						{trendValue !== 0 && trendContent}
					</Stack>

					<Stack flex={1} direction="column" justifyContent={"flex-end"} width={"100%"} spacing={2}>
						<Stack>{graph && graph}</Stack>
						{highlight && (
							<Typography variant="caption" color="secondary" textAlign="right">
								{highlight}
							</Typography>
						)}

						{coverage && (
							<Typography variant="caption" color="secondary" textAlign="right">
								{`${t("common:coverage percentage")}: ${formatNumber(coverage.value, "decimal", 0, 2)}${coverage.measure}`}
							</Typography>
						)}
					</Stack>
				</Stack>
			}
		/>
	);
};

export default NumericValueWidget;
